import merge from 'lodash.merge';
import omit from 'lodash.omit';
import { getClientId } from 'util/client-id';
import { getMerchantId } from 'util/merchant-id';
import {
  Admin,
  AdminAccountAuthorization,
  CreateEmployeeArgs,
  GetAdminResult,
  UpdateEmployeeArgs,
} from './client-domain.types';

export const transformPaymentFeeForUpdate = paymentFee => {
  if (!paymentFee && paymentFee !== 0) {
    return 300;
  }

  return paymentFee * 1000;
};

export const transformProcessingRateForUpdate = processingRate => {
  if (!processingRate && processingRate !== 0) {
    return '2.9';
  }

  return processingRate.toString();
};

export const transformUpdateClientConfig = originalBody => {
  if (!originalBody) return originalBody;

  const bodyCopy = JSON.parse(JSON.stringify(originalBody));

  if (!!bodyCopy?.incentivioPayments) {
    bodyCopy.incentivioPayments.paymentFee = transformPaymentFeeForUpdate(
      bodyCopy.incentivioPayments.paymentFee,
    );
    bodyCopy.incentivioPayments.processingRate =
      transformProcessingRateForUpdate(
        bodyCopy.incentivioPayments.processingRate,
      );
  }

  return bodyCopy;
};

export const getEmployeeAuthInfo = (
  employee: GetAdminResult,
): {
  isSuperAdmin: boolean;
  isClientAdmin: boolean;
  isWorkspaceAdmin: boolean;
  authorizedStores: string[];
} => {
  const allAuth = {
    isSuperAdmin: false,
    isClientAdmin: false,
    isWorkspaceAdmin: false,
    authorizedStores: [],
  };

  if (!employee.adminAccountAuthorization) return allAuth;

  const client = employee.adminAccountAuthorization.clients.find(
    client => client.id === getClientId(),
  );

  if (employee.adminAccountAuthorization.all) {
    allAuth.isSuperAdmin = true;
    allAuth.authorizedStores = ['ALL'];
    // while not technically true, this helps with the logic in the UI
    allAuth.isClientAdmin = true;
    return allAuth;
  }

  if (client?.all) {
    allAuth.isClientAdmin = true;
    allAuth.authorizedStores = ['ALL'];
    return allAuth;
  }

  const merchant = client?.merchants.find(
    merchant => merchant.id === getMerchantId(),
  );

  if (merchant?.all) {
    allAuth.isWorkspaceAdmin = true;
    allAuth.authorizedStores = ['ALL'];
    return allAuth;
  }

  allAuth.authorizedStores =
    merchant?.locations.map(location => location.id) ?? [];
  return allAuth;
};

export const transformAdminResponse = (response: GetAdminResult): Admin => {
  let allAuth = getEmployeeAuthInfo(response);

  return {
    ...response,
    userRoleId: response.role.id,
    ...allAuth,
  };
};

export const transformAdminRequest = (
  body: CreateEmployeeArgs | UpdateEmployeeArgs,
) =>
  omit(
    {
      ...body,
      adminAccountAuthorization: createAdminAccountAuthorization(body),
    },
    ['storeManagementTier', 'authorizedStores', 'confirmPassword'],
  );

export const createAdminAccountAuthorization = (
  body: CreateEmployeeArgs | UpdateEmployeeArgs,
) => {
  const adminAccountAuthorization: Partial<AdminAccountAuthorization> =
    body.adminAccountAuthorization
      ? merge({}, body.adminAccountAuthorization)
      : {
          all: false,
          clients: [
            {
              id: getClientId(),
              all: false,
              merchants: [
                {
                  id: getMerchantId(),
                  all: false,
                  locations: [],
                },
              ],
            },
          ],
        };

  let client = adminAccountAuthorization.clients.find(
    client => client.id === getClientId(),
  );

  let merchant = client?.merchants.find(
    merchant => merchant.id === getMerchantId(),
  );

  if (!client) {
    merchant = { id: getMerchantId(), locations: [], all: false };
    client = { id: getClientId(), merchants: [merchant], all: false };
    adminAccountAuthorization.clients.push(client);
  } else if (!merchant) {
    merchant = { id: getMerchantId(), locations: [], all: false };
    client.merchants.push(merchant);
  }

  if (body.isSuperAdmin) {
    adminAccountAuthorization.all = true;
  } else if (body.isClientAdmin) {
    adminAccountAuthorization.all = false;
    client.all = true;
    merchant.locations = [];
  } else if (body.authorizedStores.includes('ALL')) {
    adminAccountAuthorization.all = false;
    client.all = false;
    merchant.all = true;
    merchant.locations = [];
  } else {
    adminAccountAuthorization.all = false;
    client.all = false;
    merchant.all = false;
    merchant.locations = body.authorizedStores.map(location => ({
      id: location,
    }));
  }

  return adminAccountAuthorization;
};
